import React, { useState, useEffect, useRef } from "react";
import ManagerSidebar from "../../../components/Sidebar/ManagerSidebar";
import { supabase } from "../../../supabase";
import "./SlidesManagement.css"; // Importing external CSS file

const BASE_URL = "https://ikaze-thouse-backend.vercel.app";

export default function SlidesManagement() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", file: null });
  const [editingId, setEditingId] = useState(null);
  
  // Ref to programmatically clear file input element
  const fileInputRef = useRef(null);

  // Fetch slides from backend
  const fetchSlides = async () => {
    try {
      setLoading(true);
      // Matches: router.get('/all', getSlides);
      const res = await fetch(`${BASE_URL}/api/slides/all`);
      if (!res.ok) throw new Error("Failed to fetch slides");
      const data = await res.json();
      setSlides(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  // Upload image to Supabase and get public URL
  const uploadImage = async (file) => {
    if (!file) return null;

    const fileName = `${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("items").upload(`uploads/${fileName}`, file);
    if (error) throw error;

    return `https://cwrvtzjlxapzlgqzwnfn.supabase.co/storage/v1/object/public/items/uploads/${fileName}`;
  };

  // Handle create or update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Build payload matching your Slide schema requirements
      const payload = {
        title: form.title,
        description: form.description,
      };

      // Upload image if a new file is targeted, otherwise pass down existing string if editing
      if (form.file) {
        const imageUrl = await uploadImage(form.file);
        payload.image_url = imageUrl;
      } else if (editingId) {
        // Retain the current slide image asset link if updating text details only
        const activeSlide = slides.find(s => s.id === editingId);
        payload.image_url = activeSlide ? activeSlide.image_url : null;
      }

      // Check if image link exists before dispatching request to server side rules
      if (!payload.image_url) {
        throw new Error("An image file is required.");
      }

      // Base routing addresses matching slide controller setups
      const endpoint = editingId ? `/api/slides/${editingId}` : "/api/slides/add";
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(BASE_URL + endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Save operation failed.");
      }
      
      const savedSlide = await res.json();

      if (editingId) {
        setSlides(slides.map(s => s.id === editingId ? savedSlide : s));
        setEditingId(null);
      } else {
        setSlides([savedSlide, ...slides]);
      }

      resetForm();

    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset helper
  const resetForm = () => {
    setEditingId(null);
    setForm({ title: "", description: "", file: null });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Start editing
  const startEdit = (slide) => {
    setEditingId(slide.id);
    setForm({
      title: slide.title || "",
      description: slide.description || "",
      file: null
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Delete slide
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this slide?")) return;
    try {
      // Matches: router.delete('/:id', deleteSlide);
      const res = await fetch(`${BASE_URL}/api/slides/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete operation failed");
      setSlides(slides.filter(s => s.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="manager-container">
      <ManagerSidebar />
      
      <div className="main-content">
        <header className="content-header">
          <div>
            <h1>Hero Slides Management</h1>
            <p className="subtitle">Create, monitor, update and organize your frontend slider imagery assets.</p>
          </div>
        </header>

        {/* Dynamic Form Card */}
        <div className="form-card">
          <div className="form-card-header">
            <h3>{editingId ? "✏️ Edit Slide Details" : "➕ Add New Slide Banner"}</h3>
          </div>
          
          <form onSubmit={handleSubmit} className="managed-form">
            <div className="form-grid-slides">
              <div className="input-group">
                <label>Slide Title</label>
                <input 
                  type="text"
                  placeholder="e.g. Modern Luxury Villa"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div className="input-group">
                <label>Slide Description / Subtitle</label>
                <input 
                  type="text"
                  placeholder="e.g. Experience pristine comfort starting from $2500/mo"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="input-group">
                <label>Banner Image Asset {!editingId && <span style={{ color: '#dc2626' }}>*</span>}</label>
                <input 
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={e => setForm({ ...form, file: e.target.files[0] })}
                  required={!editingId}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Processing..." : editingId ? "Update Slide" : "Publish Slide Banner"}
              </button>
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Slides Datatable Container */}
        <div className="table-card">
          <div className="table-header">
            <h3>Active Carousel Slides ({slides.length})</h3>
          </div>
          <div className="table-responsive">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Title</th>
                  <th>Description Info</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {slides.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="empty-state">
                      {loading ? "Loading slider records..." : "No active presentation slides uploaded yet."}
                    </td>
                  </tr>
                ) : (
                  slides.map(slide => (
                    <tr key={slide.id}>
                      <td>
                        <div className="image-preview-wrapper wide-banner-preview">
                          {slide.image_url ? (
                            <img src={slide.image_url} className="item-img" alt={slide.title || "Slide"} />
                          ) : (
                            <div className="image-placeholder">No Image Link</div>
                          )}
                        </div>
                      </td>
                      <td className="font-medium text-dark">{slide.title || <em style={{ color: "#94a3b8" }}>Untitled</em>}</td>
                      <td style={{ color: "#475569" }}>{slide.description || <em style={{ color: "#94a3b8" }}>No structural details provided</em>}</td>
                      <td>
                        <div className="action-buttons-group">
                          <button onClick={() => startEdit(slide)} className="btn-table btn-edit">
                            Edit
                          </button>
                          <button onClick={() => handleDelete(slide.id)} className="btn-table btn-delete">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}